'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const testimonials = [
{
  id: 1,
  quote: "Baninvest has been our go-to supplier for Scania truck parts for over 5 years. Their stock is always ready and the prices beat anyone else in Kampala. We've never had a breakdown delay because of parts availability.",
  name: 'Robert Ochieng',
  title: 'Fleet Manager',
  company: 'Nile Express Logistics Ltd',
  location: 'Kampala, Uganda',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1540fd57e-1766783765895.png",
  rating: 5,
  brand: 'Scania'
},
{
  id: 2,
  quote: "We import BPW trailer parts through Baninvest for our workshop in Kigali. The quality is consistent and they handle cross-border documentation seamlessly. Delivery to Rwanda takes just 2–3 days.",
  name: 'Diane Uwimana',
  title: 'Workshop Owner',
  company: 'Kigali Heavy Transport Services',
  location: 'Kigali, Rwanda',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a9da9010-1766783767150.png",
  rating: 5,
  brand: 'BPW Trailers'
},
{
  id: 3,
  quote: "As a Mercedes Benz truck operator in South Sudan, sourcing quality parts was always a challenge. Baninvest changed that — they stock genuine-equivalent parts and ship to Juba within the week.",
  name: 'Emmanuel Lado',
  title: 'Transport Entrepreneur',
  company: 'Lado Trans Co. Ltd',
  location: 'Juba, South Sudan',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1cb52295c-1769666774134.png",
  rating: 5,
  brand: 'Mercedes Benz'
}];


export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const t = testimonials?.[active];

  return (
    <section id="testimonials" className="py-24 bg-brand-bg overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden" data-reveal>
          <p className="text-brand-red text-xs font-heading font-black uppercase tracking-widest mb-3">
            Customer Stories
          </p>
          <h2 className="font-heading font-black text-brand-dark text-3xl md:text-5xl tracking-tight">
            Trusted by Fleets Across{' '}
            <span className="text-gradient-red-blue">East Africa</span>
          </h2>
        </div>

        {/* Testimonial Cards Grid — staggered fade-in */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {testimonials?.map((item, i) => (
            <button
              key={item?.id}
              onClick={() => setActive(i)}
              className={`testimonial-card reveal-hidden stagger-${i + 1} text-left w-full ${
                i === active ? 'testimonial-card-active' : ''
              }`}
              data-reveal
              aria-label={`View testimonial from ${item?.name}`}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {Array.from({ length: item?.rating })?.map((_, si) => (
                  <Icon key={si} name="StarIcon" size={14} className="text-brand-yellow" variant="solid" />
                ))}
              </div>
              {/* Quote */}
              <blockquote className="font-body text-sm leading-relaxed text-brand-dark/80 mb-5 line-clamp-4">
                &ldquo;{item?.quote}&rdquo;
              </blockquote>
              {/* Author */}
              <div className="flex items-center gap-3 mt-auto">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-brand-border">
                  <AppImage
                    src={item?.avatar}
                    alt={`${item?.name} from ${item?.company}`}
                    width={40}
                    height={40}
                    className="object-cover w-full h-full"
                  />
                </div>
                <div>
                  <p className="font-heading font-bold text-brand-dark text-sm">{item?.name}</p>
                  <p className="font-body text-brand-muted text-xs">{item?.title} · {item?.location}</p>
                </div>
              </div>
              {/* Brand tag */}
              <div className="mt-4 inline-block px-3 py-1 rounded-full bg-brand-red/10 text-brand-red text-xs font-heading font-bold">
                {item?.brand} Parts User
              </div>
            </button>
          ))}
        </div>

        {/* Active testimonial expanded view */}
        <div
          className="bg-brand-dark rounded-[32px] sm:rounded-[48px] p-8 sm:p-10 md:p-16 text-white text-center max-w-4xl mx-auto shadow-2xl relative overflow-hidden reveal-hidden-scale"
          data-reveal>
          
          {/* Background decoration */}
          <div
            className="absolute top-0 left-0 w-64 h-64 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #C41E3A, transparent)', transform: 'translate(-30%, -30%)' }}
            aria-hidden="true" />
          
          <div
            className="absolute bottom-0 right-0 w-64 h-64 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #1B3A8C, transparent)', transform: 'translate(30%, 30%)' }}
            aria-hidden="true" />

          {/* Stars */}
          <div className="flex justify-center gap-1 mb-6">
            {Array.from({ length: t?.rating })?.map((_, i) =>
            <Icon key={i} name="StarIcon" size={18} className="text-brand-yellow" variant="solid" />
            )}
          </div>

          {/* Quote */}
          <blockquote className="font-heading font-semibold text-lg sm:text-xl md:text-3xl italic leading-snug tracking-tight mb-8 relative z-10">
            &ldquo;{t?.quote}&rdquo;
          </blockquote>

          {/* Attribution */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-brand-yellow flex-shrink-0">
              <AppImage
                src={t?.avatar || ''}
                alt={`${t?.name} testimonial`}
                width={56}
                height={56}
                className="object-cover w-full h-full"
              />
            </div>
            <div className="text-center sm:text-left">
              <p className="font-heading font-black text-brand-yellow uppercase tracking-widest text-sm">
                {t?.name}
              </p>
              <p className="text-white/40 font-body text-xs uppercase tracking-widest mt-1">
                {t?.title} · {t?.company} · {t?.location}
              </p>
              <span className="inline-block mt-2 px-3 py-1 rounded-full bg-white/10 text-white/60 text-xs font-heading font-bold">
                {t?.brand} Parts User
              </span>
            </div>
          </div>

          {/* Navigation dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials?.map((_, i) =>
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`h-1.5 rounded-full transition-all ${
              i === active ? 'w-8 bg-brand-yellow' : 'w-3 bg-white/20'}`
              }
              aria-label={`Testimonial ${i + 1}`} />

            )}
          </div>
        </div>
      </div>
    </section>
  );

}